// src/pages/KpisOverview.jsx
// Vista general de KPIs — el "scorecard" con una tarjeta por cada KPI
// registrado en la configuración del backend. Al crecer el número de
// KPIs, esta vista no necesita cambios: solo recorre lo que el
// endpoint /overview le entrega.
//
// Campos opcionales que puede regresar calcularResumen:
//   formato: 'moneda'   → el valor se muestra como $1,478,062
//   resumen_texto       → una línea de contexto debajo del valor

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { kpiService } from '../services/api';

const COLORES_SEMAFORO = {
  verde: '#3E7C5A',
  amarillo: '#C08A2B',
  rojo: '#B23A48',
  gris: '#8A7690',
};

const FONDO_SEMAFORO = {
  verde: '#E8F1EC',
  amarillo: '#F9F0DD',
  rojo: '#F7E5E7',
  gris: '#F0E9E0',
};

const FLECHA_TENDENCIA = { up: '▲', down: '▼', flat: '—' };

/** Valor de la tarjeta con su unidad, respetando el formato que pida el KPI */
function formatearValor(t, valor = t.valor) {
  if (valor === null || valor === undefined) return '—';
  if (t.formato === 'moneda') return `$${Math.round(Number(valor)).toLocaleString('es-MX')}${t.unidad || ''}`;
  return `${valor}${t.unidad || ''}`;
}

/**
 * Mini-gráfica de tendencia interactiva (SVG puro, sin librería).
 * "datos": arreglo de números, del más viejo al más nuevo.
 * "alto"/"interactiva": la versión chica del card usa alto=34 sin
 * etiquetas; la versión del modal usa alto mayor con etiquetas de eje.
 */
function Sparkline({ datos, color, alto = 34, unidad = '', interactiva = true }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!Array.isArray(datos) || datos.length < 2) return null;

  const w = 100;
  const pad = 4;
  const max = Math.max(...datos);
  const min = Math.min(...datos);
  const rango = max - min || 1;

  const puntos = datos.map((v, i) => {
    const x = (i / (datos.length - 1)) * w;
    const y = pad + (alto - pad * 2) - ((v - min) / rango) * (alto - pad * 2);
    return { x, y, v };
  });

  const puntosStr = puntos.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `0,${alto} ${puntosStr} ${w},${alto}`;

  function handleMove(e) {
    if (!interactiva) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xRel = ((e.clientX - rect.left) / rect.width) * w;
    let idxCercano = 0;
    let distMin = Infinity;
    puntos.forEach((p, i) => {
      const d = Math.abs(p.x - xRel);
      if (d < distMin) { distMin = d; idxCercano = i; }
    });
    setHoverIdx(idxCercano);
  }

  const activo = hoverIdx !== null ? puntos[hoverIdx] : puntos[puntos.length - 1];

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${w} ${alto}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: `${alto}px`, display: 'block', cursor: interactiva ? 'crosshair' : 'default' }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <polyline points={area} fill={color} fillOpacity="0.12" stroke="none" />
        <polyline points={puntosStr} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {interactiva && hoverIdx !== null && (
          <line x1={activo.x} y1={0} x2={activo.x} y2={alto} stroke={color} strokeWidth="0.6" strokeDasharray="2,2" opacity="0.5" />
        )}
        <circle cx={activo.x} cy={activo.y} r={hoverIdx !== null ? 2.8 : 2.2} fill={color} />
      </svg>
      {interactiva && hoverIdx !== null && (
        <div
          style={{
            ...stylesSparkline.tooltip,
            left: `${activo.x}%`,
            transform: activo.x > 70 ? 'translateX(-100%)' : activo.x < 15 ? 'translateX(0)' : 'translateX(-50%)',
          }}
        >
          {activo.v.toLocaleString('es-MX', { maximumFractionDigits: 2 })}{unidad}
        </div>
      )}
    </div>
  );
}

const stylesSparkline = {
  tooltip: {
    position: 'absolute',
    top: '-26px',
    background: '#2A1830',
    color: '#fff',
    fontSize: '10.5px',
    fontWeight: 700,
    padding: '3px 7px',
    borderRadius: '5px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: 5,
  },
};

/** Modal con la gráfica completa de un KPI + acceso directo al detalle. */
function ModalGrafica({ tarjeta, onCerrar }) {
  if (!tarjeta) return null;
  const color = COLORES_SEMAFORO[tarjeta.semaforo] || '#8A7690';

  return (
    <div style={styles.modalFondo} onClick={onCerrar}>
      <div style={styles.modalCaja} onClick={(e) => e.stopPropagation()}>
        <button onClick={onCerrar} style={styles.modalCerrar}>×</button>
        <div style={styles.modalTitulo}>{tarjeta.titulo}</div>
        <div style={styles.modalValorFila}>
          <span style={{ ...styles.tarjetaValor, fontSize: '34px' }}>
            {formatearValor(tarjeta)}
          </span>
          <span style={{ ...styles.badge, color, background: FONDO_SEMAFORO[tarjeta.semaforo] || '#F0E9E0' }}>
            {FLECHA_TENDENCIA[tarjeta.tendencia]}
          </span>
        </div>
        {tarjeta.meta !== null && <div style={styles.tarjetaMeta}>Meta: {formatearValor(tarjeta, tarjeta.meta)}</div>}

        <div style={styles.modalGraficaWrap}>
          <Sparkline datos={tarjeta.historial} color={color} alto={140} unidad={tarjeta.unidad} />
          <div style={styles.modalEjeFila}>
            <span>hace {tarjeta.historial.length - 1} periodos</span>
            <span>más reciente</span>
          </div>
        </div>

        <Link to={tarjeta.link} style={styles.modalBotonDetalle} onClick={onCerrar}>
          Ver detalle completo →
        </Link>
      </div>
    </div>
  );
}

export default function KpisOverview() {
  const [tarjetas, setTarjetas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [sincronizando, setSincronizando] = useState(null); // key del KPI en proceso
  const [modalTarjeta, setModalTarjeta] = useState(null);

  async function cargar() {
    try {
      setCargando(true);
      const res = await kpiService.overview();
      setTarjetas(res.data.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('No se pudo cargar el resumen general de KPIs.');
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  async function handleSync(e, key) {
    e.preventDefault();
    e.stopPropagation();
    try {
      setSincronizando(key);
      await kpiService.sync(key);
      await cargar();
    } catch (err) {
      console.error(`Error sincronizando ${key}:`, err);
      alert(`No se pudo sincronizar: ${err.response?.data?.error || err.message}`);
    } finally {
      setSincronizando(null);
    }
  }

  function handleAbrirModal(e, tarjeta) {
    e.preventDefault();
    e.stopPropagation();
    setModalTarjeta(tarjeta);
  }

  if (cargando) return <div style={styles.contenedor}>Cargando...</div>;
  if (error) return <div style={{ ...styles.contenedor, color: '#B23A48' }}>{error}</div>;

  const porArea = tarjetas.reduce((acc, t) => {
    acc[t.area] = acc[t.area] || [];
    acc[t.area].push(t);
    return acc;
  }, {});

  let indiceGlobal = 0;

  return (
    <div style={styles.contenedor}>
      <style>{ESTILOS_ANIMACION}</style>
      <h1 style={styles.titulo}>Resumen General de KPIs</h1>

      {Object.entries(porArea).map(([area, items]) => (
        <div key={area} style={{ marginBottom: '32px' }}>
          <h2 style={styles.areaTitulo}>{area}</h2>
          <div style={styles.grid}>
            {items.map((t) => {
              const colorSemaforo = COLORES_SEMAFORO[t.semaforo] || '#8A7690';
              const demora = indiceGlobal * 35;
              indiceGlobal += 1;
              const tieneHistorial = Array.isArray(t.historial) && t.historial.length >= 2;

              return (
                <Link key={t.key} to={t.link} style={styles.tarjetaLink}>
                  <div className="kpi-tarjeta" style={{ ...styles.tarjeta, animationDelay: `${demora}ms` }}>
                    <div style={{ ...styles.barraColor, background: colorSemaforo }} />
                    <div style={styles.tarjetaTitulo}>{t.titulo}</div>
                    <div style={styles.tarjetaValorFila}>
                      <span style={{ ...styles.tarjetaValor, ...(t.formato === 'moneda' ? styles.tarjetaValorMoneda : {}) }}>
                        {formatearValor(t)}
                      </span>
                      <span style={{ ...styles.badge, color: colorSemaforo, background: FONDO_SEMAFORO[t.semaforo] || '#F0E9E0' }}>
                        {FLECHA_TENDENCIA[t.tendencia]}
                      </span>
                    </div>

                    {t.resumen_texto && <div style={styles.tarjetaDetalle}>{t.resumen_texto}</div>}

                    {tieneHistorial && (
                      <div
                        style={styles.sparklineWrap}
                        onClick={(e) => handleAbrirModal(e, t)}
                        title="Ver gráfica completa"
                      >
                        <Sparkline datos={t.historial} color={colorSemaforo} unidad={t.unidad} />
                      </div>
                    )}

                    {t.meta !== null && <div style={styles.tarjetaMeta}>Meta: {formatearValor(t, t.meta)}</div>}

                    {typeof t.detalle === 'string' && t.detalle && (
                      <div style={styles.tarjetaDetalle}>{t.detalle}</div>
                    )}

                    {Array.isArray(t.detalle) && t.detalle.length > 0 && (
                      <ul style={styles.listaDetalle}>
                        {t.detalle.map((lote) => (
                          <li key={`${lote.codigo_upc}-${lote.numero_lote}`} style={styles.itemDetalle}>
                            <span style={styles.itemDetalleTexto}>{lote.articulo}</span>
                            <span style={{ ...styles.itemDetalleDias, color: lote.semaforo === 'rojo' ? COLORES_SEMAFORO.rojo : COLORES_SEMAFORO.amarillo }}>
                              {lote.valor !== null && lote.valor !== undefined && (
                                <span style={styles.itemDetalleValor}>${Math.round(Number(lote.valor)).toLocaleString('es-MX')} · </span>
                              )}
                              {lote.dias_caducidad}d
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {t.error && <div style={styles.tarjetaError}>⚠ Error al calcular</div>}

                    {!t.sinSync && (
                      <button onClick={(e) => handleSync(e, t.key)} disabled={sincronizando === t.key} style={styles.botonSync}>
                        {sincronizando === t.key ? 'Sincronizando...' : '↻ Sincronizar ahora'}
                      </button>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}

      {tarjetas.length === 0 && (
        <p style={{ color: '#8A7690' }}>Todavía no hay KPIs configurados. Se irán agregando conforme se sincronicen.</p>
      )}

      <ModalGrafica tarjeta={modalTarjeta} onCerrar={() => setModalTarjeta(null)} />
    </div>
  );
}

const ESTILOS_ANIMACION = `
@keyframes kpiFadeInUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.kpi-tarjeta {
  opacity: 0;
  animation: kpiFadeInUp 0.4s ease-out forwards;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.kpi-tarjeta:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(74,30,92,0.14);
}
`;

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '24px' },
  areaTitulo: { fontSize: '13px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#6E4F80', marginBottom: '12px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '16px' },
  tarjetaLink: { textDecoration: 'none', color: 'inherit' },
  tarjeta: { position: 'relative', background: '#fff', border: '1px solid #E6DDD2', borderRadius: '14px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', cursor: 'pointer' },
  barraColor: { position: 'absolute', top: 0, left: 0, right: 0, height: '4px', borderRadius: '14px 14px 0 0' },
  tarjetaTitulo: { fontSize: '12px', color: '#8A7690', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '10px', minHeight: '32px', fontWeight: 600 },
  tarjetaValorFila: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px', flexWrap: 'wrap' },
  tarjetaValor: { fontSize: '27px', fontWeight: 700, color: '#2A1830', letterSpacing: '-0.5px' },
  tarjetaValorMoneda: { fontSize: '23px' },
  badge: { fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' },
  sparklineWrap: { margin: '4px 0 8px', cursor: 'zoom-in' },
  tarjetaMeta: { fontSize: '11px', color: '#8A7690' },
  tarjetaDetalle: { fontSize: '10.5px', color: '#6E4F80', marginTop: '4px', fontWeight: 500 },
  tarjetaError: { fontSize: '11px', color: '#B23A48', marginTop: '6px' },
  listaDetalle: { listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '3px' },
  itemDetalle: { display: 'flex', justifyContent: 'space-between', gap: '6px', fontSize: '10.5px', color: '#6E4F80' },
  itemDetalleTexto: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  itemDetalleDias: { fontWeight: 700, flexShrink: 0 },
  itemDetalleValor: { fontWeight: 500, color: '#8A7690' },
  botonSync: { marginTop: '12px', width: '100%', padding: '6px 8px', fontSize: '11px', fontWeight: 500, color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: '6px', cursor: 'pointer' },

  modalFondo: { position: 'fixed', inset: 0, background: 'rgba(42,24,48,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' },
  modalCaja: { position: 'relative', background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(42,24,48,0.3)' },
  modalCerrar: { position: 'absolute', top: '14px', right: '14px', width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: '#F0E9E0', color: '#6E4F80', fontSize: '16px', cursor: 'pointer', lineHeight: 1 },
  modalTitulo: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#2A1830', marginBottom: '10px', paddingRight: '24px' },
  modalValorFila: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' },
  modalGraficaWrap: { margin: '20px 0 8px', padding: '12px 8px 4px', background: '#FAF8F5', borderRadius: '10px' },
  modalEjeFila: { display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8A7690', marginTop: '6px' },
  modalBotonDetalle: { display: 'block', textAlign: 'center', marginTop: '18px', padding: '11px', fontSize: '13px', fontWeight: 600, color: '#fff', background: '#6A2C75', borderRadius: '8px', textDecoration: 'none' },
};